import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { billingAPI } from "../services/api";
import {
  Users,
  TrendingUp,
  DollarSign,
  Download,
  Mail,
  FileText,
  Activity,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const formatCurrency = (amount = 0) =>
  new Intl.NumberFormat("en-CH", {
    style: "currency",
    currency: "CHF",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);

const formatSeconds = (seconds = 0) => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  if (hrs > 0) return `${hrs}h ${mins}m`;
  if (mins > 0) return `${mins}m ${secs}s`;
  return `${secs}s`;
};

const UserRow = ({ user, onGenerateInvoice, onViewInvoices, isGenerating }) => {
  const [expanded, setExpanded] = useState(false);

  // Définir le style du badge selon le statut de la facture
  const getInvoiceStatusBadge = () => {
    if (!user.latestInvoice) {
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-500/30 bg-slate-500/10 px-2.5 py-1 text-xs font-semibold text-slate-300">
          <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
          Aucune
        </span>
      );
    }

    const statusConfig = {
      paid: {
        class: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
        label: "Payée",
        icon: (
          <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
        ),
      },
      sent: {
        class: "border-blue-500/30 bg-blue-500/10 text-blue-300",
        label: "Envoyée",
        icon: (
          <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
            <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
            <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
          </svg>
        ),
      },
      overdue: {
        class: "border-orange-500/30 bg-orange-500/10 text-orange-300",
        label: "En retard",
        icon: (
          <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        ),
      },
      draft: {
        class: "border-slate-500/30 bg-slate-500/10 text-slate-300",
        label: "Brouillon",
        icon: (
          <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
            <path
              fillRule="evenodd"
              d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z"
              clipRule="evenodd"
            />
          </svg>
        ),
      },
      cancelled: {
        class: "border-red-500/30 bg-red-500/10 text-red-300",
        label: "Annulée",
        icon: (
          <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
        ),
      },
    };

    const config =
      statusConfig[user.latestInvoice.status] || statusConfig.draft;

    return (
      <span
        className={`inline-flex items-center gap-1.5 rounded-full border ${config.class} px-2.5 py-1 text-xs font-semibold`}
      >
        {config.icon}
        {config.label}
      </span>
    );
  };

  return (
    <div className="rounded-2xl border border-white/5 bg-white/5 overflow-hidden">
      <button
        className="flex w-full items-center gap-4 px-6 py-4 text-left hover:bg-white/5 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-white">
            {user.firstName} {user.lastName}
          </p>
          <p className="text-xs text-slate-400">{user.email}</p>
        </div>
        <div className="text-right">
          <p className="text-sm text-slate-300">
            {formatSeconds(user.totalAudioSeconds || 0)}
          </p>
          <p className="text-xs text-slate-400">
            {user.totalEvents || 0} événements
          </p>
        </div>
        <div className="text-right min-w-[100px]">
          <p className="font-semibold text-white">
            {formatCurrency(user.totalCost || 0)}
          </p>
          <p className="text-xs text-emerald-400">Ce mois</p>
        </div>
        <div className="min-w-[100px] flex justify-center">
          {getInvoiceStatusBadge()}
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onGenerateInvoice(user.userId);
          }}
          disabled={isGenerating === user.userId}
          className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-1.5 text-xs font-semibold text-emerald-300 transition hover:bg-emerald-500/20 disabled:opacity-60 disabled:cursor-not-allowed"
          title="Générer la facture du mois en cours"
        >
          {isGenerating === user.userId ? (
            <div className="h-3 w-3 animate-spin rounded-full border border-emerald-300/40 border-t-emerald-300" />
          ) : (
            <FileText className="h-3 w-3" />
          )}
        </button>
        {expanded ? (
          <ChevronUp className="h-4 w-4 text-slate-400" />
        ) : (
          <ChevronDown className="h-4 w-4 text-slate-400" />
        )}
      </button>

      {expanded && (
        <div className="border-t border-white/10 px-6 py-4 bg-slate-950/30">
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => onGenerateInvoice(user.userId)}
              disabled={isGenerating === user.userId}
              className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-4 py-2 text-sm font-semibold text-white/90 transition hover:bg-white/10 disabled:opacity-60"
            >
              {isGenerating === user.userId ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
              ) : (
                <FileText className="h-4 w-4" />
              )}
              Générer facture
            </button>
            <button
              onClick={() =>
                onViewInvoices(
                  user.userId,
                  `${user.firstName} ${user.lastName}`,
                )
              }
              className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-4 py-2 text-sm font-semibold text-white/90 transition hover:bg-white/10"
            >
              <Download className="h-4 w-4" />
              Voir factures
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const AllUsersBilling = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [allUsersData, setAllUsersData] = useState(null);
  const [generatingInvoiceForUser, setGeneratingInvoiceForUser] =
    useState(null);
  const [showInvoicesModal, setShowInvoicesModal] = useState(false);
  const [selectedUserInvoices, setSelectedUserInvoices] = useState(null);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  useEffect(() => {
    fetchAllUsersConsumption();
  }, []);

  const fetchAllUsersConsumption = async () => {
    try {
      setLoading(true);
      const data = await billingAPI.getAllUsersConsumption({ limit: 100 });
      setAllUsersData(data);
    } catch (error) {
      console.error("Failed to fetch all users consumption:", error);
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const generateInvoiceForUser = async (userId) => {
    if (generatingInvoiceForUser) return;
    try {
      setGeneratingInvoiceForUser(userId);
      const now = new Date();
      const periodStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const periodEnd = now;
      const result = await billingAPI.generateInvoiceForUser(userId, {
        periodStart: periodStart.toISOString(),
        periodEnd: periodEnd.toISOString(),
      });
      toast.success(
        `Facture ${result.invoice.invoiceNumber} générée avec succès`,
      );
      fetchAllUsersConsumption();
    } catch (error) {
      console.error("Error generating invoice for user:", error);
      const errorMsg =
        error.response?.data?.error ||
        "Erreur lors de la génération de la facture";
      toast.error(errorMsg);
    } finally {
      setGeneratingInvoiceForUser(null);
    }
  };

  const viewUserInvoices = async (userId, userName) => {
    try {
      setLoadingInvoices(true);
      setShowInvoicesModal(true);
      const result = await billingAPI.getUserInvoices(userId);
      setSelectedUserInvoices({ userName, ...result });
    } catch (error) {
      console.error("Error fetching user invoices:", error);
      toast.error("Erreur lors du chargement des factures");
      setShowInvoicesModal(false);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const updateInvoiceStatus = async (invoiceId, newStatus) => {
    try {
      await billingAPI.updateInvoiceStatus(invoiceId, newStatus);
      toast.success(`Statut de la facture mis à jour: ${newStatus}`);

      // Refresh invoice list
      if (selectedUserInvoices?.user?.id) {
        const result = await billingAPI.getUserInvoices(
          selectedUserInvoices.user.id,
        );
        setSelectedUserInvoices({
          userName: selectedUserInvoices.userName,
          ...result,
        });
      }

      // Refresh all users list to update status badge
      fetchAllUsersConsumption();
    } catch (error) {
      console.error("Error updating invoice status:", error);
      toast.error("Erreur lors de la mise à jour du statut");
    }
  };

  const closeInvoicesModal = () => {
    setShowInvoicesModal(false);
    setSelectedUserInvoices(null);
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] bg-[#020617] text-white flex items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-white/20 border-t-white" />
      </div>
    );
  }

  const totalUsers = allUsersData?.users?.length || 0;
  const totalRevenue = allUsersData?.totalRevenue || 0;
  const totalAudioSeconds = allUsersData?.totalAudioSeconds || 0;

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#030816] text-white">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950" />
        <div className="absolute -top-16 left-1/3 h-96 w-96 rounded-full bg-cyan-500/20 blur-[140px]" />
        <div className="absolute bottom-[-10%] right-0 h-[26rem] w-[26rem] rounded-full bg-indigo-500/20 blur-[140px]" />
      </div>

      <div className="relative z-10 px-4 pt-6 pb-12 sm:px-8 lg:px-12 space-y-8">
        {/* Header */}
        <div className="rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur-xl shadow-2xl shadow-cyan-500/10">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-12 w-12 flex items-center justify-center rounded-2xl bg-gradient-to-r from-cyan-500 to-indigo-500">
              <Users className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-semibold text-white">
                Gestion Globale des Utilisateurs
              </h1>
              <p className="text-sm text-slate-300">
                Consommation et facturation de tous les clients
              </p>
            </div>
          </div>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="rounded-3xl border border-white/10 bg-slate-900/70 p-5 shadow-lg shadow-black/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.4em] text-slate-400">
                  Utilisateurs actifs
                </p>
                <p className="mt-3 text-3xl font-semibold text-white">
                  {totalUsers}
                </p>
                <p className="text-xs text-slate-400">Ce mois</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-2">
                <Users className="h-5 w-5 text-cyan-200" />
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-white/10 bg-slate-900/70 p-5 shadow-lg shadow-black/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.4em] text-slate-400">
                  Audio traité
                </p>
                <p className="mt-3 text-3xl font-semibold text-white">
                  {formatSeconds(totalAudioSeconds)}
                </p>
                <p className="text-xs text-slate-400">Total du mois</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-2">
                <Activity className="h-5 w-5 text-cyan-200" />
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-white/10 bg-slate-900/70 p-5 shadow-lg shadow-black/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.4em] text-slate-400">
                  Revenu total
                </p>
                <p className="mt-3 text-3xl font-semibold text-white">
                  {formatCurrency(totalRevenue)}
                </p>
                <p className="text-xs text-slate-400">Ce mois</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-2">
                <DollarSign className="h-5 w-5 text-cyan-200" />
              </div>
            </div>
          </div>
        </div>

        {/* Users list */}
        <div className="rounded-3xl border border-white/10 bg-slate-900/70 p-6 shadow-xl shadow-black/30">
          <div className="mb-6">
            <p className="text-xs uppercase tracking-[0.4em] text-cyan-200/70">
              Détails par utilisateur
            </p>
            <h3 className="text-2xl font-semibold text-white">
              Consommation et Facturation
            </h3>
          </div>

          {allUsersData?.users && allUsersData.users.length > 0 ? (
            <div className="space-y-3">
              {allUsersData.users.map((user) => (
                <UserRow
                  key={user.userId}
                  user={user}
                  onGenerateInvoice={generateInvoiceForUser}
                  onViewInvoices={viewUserInvoices}
                  isGenerating={generatingInvoiceForUser}
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Users className="h-16 w-16 text-white/20 mb-4" />
              <h3 className="text-xl font-semibold text-white">
                Aucun utilisateur actif
              </h3>
              <p className="text-sm text-slate-400 mt-2">
                Aucune consommation ce mois-ci
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Modal for user invoices */}
      {showInvoicesModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="relative w-full max-w-4xl mx-4 rounded-3xl border border-white/10 bg-slate-900 p-6 shadow-2xl">
            <button
              onClick={closeInvoicesModal}
              className="absolute top-4 right-4 rounded-full bg-white/10 p-2 hover:bg-white/20"
            >
              ✕
            </button>
            <h2 className="text-2xl font-semibold text-white mb-4">
              Factures de {selectedUserInvoices?.userName}
            </h2>
            {loadingInvoices ? (
              <div className="flex justify-center py-8">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-white" />
              </div>
            ) : (
              <div className="space-y-2">
                {selectedUserInvoices?.invoices?.length > 0 ? (
                  selectedUserInvoices.invoices.map((invoice) => {
                    const statusStyles = {
                      draft:
                        "[color-scheme:dark] rounded-lg border border-slate-500/30 bg-slate-500/10 px-3 py-1 text-sm font-semibold text-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-500/50",
                      sent: "[color-scheme:dark] rounded-lg border border-blue-500/30 bg-blue-500/10 px-3 py-1 text-sm font-semibold text-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500/50",
                      paid: "[color-scheme:dark] rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-sm font-semibold text-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/50",
                      overdue:
                        "[color-scheme:dark] rounded-lg border border-orange-500/30 bg-orange-500/10 px-3 py-1 text-sm font-semibold text-orange-300 focus:outline-none focus:ring-2 focus:ring-orange-500/50",
                      cancelled:
                        "[color-scheme:dark] rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-1 text-sm font-semibold text-red-300 focus:outline-none focus:ring-2 focus:ring-red-500/50",
                    };

                    return (
                      <div
                        key={invoice.id}
                        className="flex items-center justify-between rounded-xl border border-white/5 bg-white/5 px-4 py-3"
                      >
                        <div>
                          <p className="font-semibold text-white">
                            {invoice.invoiceNumber}
                          </p>
                          <p className="text-xs text-slate-400">
                            {new Date(invoice.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <select
                            value={invoice.status}
                            onChange={(e) =>
                              updateInvoiceStatus(invoice.id, e.target.value)
                            }
                            className={
                              statusStyles[invoice.status] || statusStyles.draft
                            }
                          >
                            <option value="draft">Brouillon</option>
                            <option value="sent">Envoyée</option>
                            <option value="paid">Payée</option>
                            <option value="overdue">En retard</option>
                            <option value="cancelled">Annulée</option>
                          </select>
                          <p className="font-semibold text-white min-w-[100px] text-right">
                            {formatCurrency(invoice.totalAmount)}
                          </p>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-center text-slate-400 py-8">
                    Aucune facture trouvée
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AllUsersBilling;
